import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';

// Types
interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  organization_name?: string;
  // Profile photo URL — Cloudinary/S3 link set by the avatar uploader on
  // ProfilePage. Optional because legacy users (signed up before the
  // feature shipped) won't have one. Sidebar/Header read this and fall
  // back to initials when absent.
  avatar_url?: string;
  is_admin: boolean;
  is_active: boolean;
  is_onboarded: boolean;
  onboarding_completed_at?: string | null;
  created_at: string;
  last_login: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; accessToken: string; refreshToken: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'TOKEN_REFRESH'; payload: { accessToken: string } };

// Initial state
const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'TOKEN_REFRESH':
      return {
        ...state,
        accessToken: action.payload.accessToken,
      };
    default:
      return state;
  }
};

// Context type
interface AuthContextType {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  // Helper functions
  login: (email: string, password?: string) => Promise<boolean>;
  requestMagicLink: (email: string) => Promise<boolean>;
  verifyMagicLink: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  getCurrentUser: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://api-beta-open-tender.nat-quest.com' // API subdomain in production
    : 'http://localhost:2024');

// Helper function to make API calls
const apiCall = async (
  endpoint: string, 
  options: RequestInit = {},
  token?: string
): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }

  return response.json();
};

// Storage helpers
const TOKEN_STORAGE_KEY = 'opentender_tokens';

const saveTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
    accessToken,
    refreshToken,
    timestamp: Date.now(),
  }));
};

const getStoredTokens = (): { accessToken: string; refreshToken: string } | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const { accessToken, refreshToken, timestamp } = JSON.parse(stored);
    
    // Check if tokens are too old (7 days)
    if (Date.now() - timestamp > 7 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
    
    return { accessToken, refreshToken };
  } catch {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
};

const clearTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Helper functions
  const login = async (email: string, password?: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      if (!password) {
        throw new Error('Password is required for login');
      }

      const response = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      // Get user info
      const userResponse = await apiCall('/auth/me', {
        headers: {
          'Authorization': `Bearer ${response.access_token}`,
        },
      });

      // Save tokens
      saveTokens(response.access_token, response.refresh_token);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: userResponse,
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        },
      });

      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Login failed' });
      return false;
    }
  };

  const requestMagicLink = async (email: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      await apiCall('/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      dispatch({ type: 'SET_LOADING', payload: false });
      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to send magic link' });
      return false;
    }
  };

  const verifyMagicLink = async (token: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await apiCall(`/auth/verify-magic-link?token=${encodeURIComponent(token)}`);

      // Get user info
      const userResponse = await apiCall('/auth/me', {
        headers: {
          'Authorization': `Bearer ${response.access_token}`,
        },
      });

      // Save tokens
      saveTokens(response.access_token, response.refresh_token);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: userResponse,
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        },
      });

      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Invalid magic link' });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (state.accessToken) {
        await apiCall('/auth/logout', {
          method: 'POST',
        }, state.accessToken);
      }
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      clearTokens();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      if (!state.refreshToken) {
        return false;
      }

      const response = await apiCall('/auth/refresh', {
        method: 'POST',
      }, state.refreshToken);

      dispatch({
        type: 'TOKEN_REFRESH',
        payload: { accessToken: response.access_token },
      });

      // Update stored tokens
      saveTokens(response.access_token, state.refreshToken);

      return true;
    } catch (error) {
      // Refresh token is invalid, logout user
      await logout();
      return false;
    }
  };

  const getCurrentUser = async (): Promise<void> => {
    try {
      if (!state.accessToken) {
        return;
      }

      const userResponse = await apiCall('/auth/me', {}, state.accessToken);

      dispatch({
        type: 'UPDATE_USER',
        payload: userResponse,
      });
    } catch (error) {
      // Token might be expired, try to refresh
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        await logout();
      }
    }
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // Local demo bypass. Double-guarded: NODE_ENV is hardcoded to
      // 'production' by react-scripts build, so this branch is stripped from
      // any real bundle and cannot be switched on by setting the env var in a
      // deployed environment. Used to demo backend-independent screens (the
      // Evaluation workspace runs entirely on local fixtures) without
      // depending on a live auth service.
      if (
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_DEV_AUTH_BYPASS === 'true'
      ) {
        console.warn('[auth] DEV bypass active — no real authentication is in effect.');
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: {
              id: 'dev-user',
              email: 'demo@opentender.ai',
              first_name: 'Abraham',
              last_name: 'Onoja',
              job_title: 'Head of Procurement',
              organization_name: 'OpenTender.AI',
              is_admin: true,
              is_active: true,
              is_onboarded: true,
              onboarding_completed_at: '2026-01-01T00:00:00Z',
            } as any,
            accessToken: 'dev-bypass',
            refreshToken: 'dev-bypass',
          },
        });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      const storedTokens = getStoredTokens();
      
      if (!storedTokens) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // Set tokens in state
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: null as any, // Will be fetched
          accessToken: storedTokens.accessToken,
          refreshToken: storedTokens.refreshToken,
        },
      });

      // Try to get current user
      try {
        const userResponse = await apiCall('/auth/me', {}, storedTokens.accessToken);
        dispatch({
          type: 'UPDATE_USER',
          payload: userResponse,
        });
      } catch (error) {
        // Try to refresh token
        try {
          const response = await apiCall('/auth/refresh', {
            method: 'POST',
          }, storedTokens.refreshToken);

          const userResponse = await apiCall('/auth/me', {}, response.access_token);

          saveTokens(response.access_token, storedTokens.refreshToken);

          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: userResponse,
              accessToken: response.access_token,
              refreshToken: storedTokens.refreshToken,
            },
          });
        } catch (refreshError) {
          // Both access and refresh tokens are invalid
          clearTokens();
          dispatch({ type: 'LOGOUT' });
        }
      }

      dispatch({ type: 'SET_LOADING', payload: false });
    };

    initializeAuth();
  }, []);

  const value: AuthContextType = {
    state,
    dispatch,
    login,
    requestMagicLink,
    verifyMagicLink,
    logout,
    refreshAccessToken,
    getCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};